import type { ReactNode } from 'react';
import { photoUrl } from '../api/client.ts';

/**
 * The tile beside a name, wherever one is drawn.
 *
 * The photograph is never cropped — not here and not on the server, where the
 * whole picture is what is stored. It is `object-fit: cover` inside whatever
 * `.avatar` it is put in, and that box's own `border-radius` is what shapes it.
 * Every tile in the app is the same rounded square; what a caller varies is the
 * size, and the radius with it — `--r-avatar` is drawn for the ordinary 42px
 * one, and the tiles far from that size scale it (8px at 30px in a group
 * thread, 26px at 96px on the profile). A caller changes the shape by changing
 * its CSS, exactly as it did while these were empty grey blocks, and nothing
 * about the stored file has to agree.
 *
 * Falling back is deliberately in this order: the photo, then whatever the
 * caller passes as children — the people glyph a group tile carries — then the
 * first letter of the name. An empty grey square is what this used to be
 * everywhere and is now only what somebody with no name at all gets.
 */
export function Avatar({
  photoId,
  name,
  className,
  children,
}: {
  photoId: string | null | undefined;
  /** Whose tile it is, for the letter shown when there is no photograph. */
  name?: string;
  /** The size and shape, e.g. `chat-row-avatar` or `group-pick-avatar`. */
  className?: string;
  /** Drawn instead of a letter — a group's people icon. */
  children?: ReactNode;
}) {
  return (
    <div className={`avatar${className ? ` ${className}` : ''}`}>
      {photoId ? (
        <img
          className="avatar-photo"
          src={photoUrl(photoId, { variant: 'thumb' })}
          // Decorative: the name it belongs to is always right beside it, and a
          // screen reader announcing it twice helps nobody.
          alt=""
          loading="lazy"
          decoding="async"
        />
      ) : (
        (children ?? (name ? <span className="avatar-initial">{initial(name)}</span> : null))
      )}
    </div>
  );
}

/**
 * The one letter a tile shows when there is no photograph on it.
 *
 * The first letter or digit rather than the first character: names here wear
 * punctuation — «Проект «Компас»», „Сайт Оли“ — and a tile with a quotation
 * mark on it says nothing about whose tile it is.
 */
export function initial(name: string): string {
  const letter = [...name].find((char) => /[\p{L}\p{N}]/u.test(char));
  return letter?.toUpperCase() ?? '?';
}
